import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Code, BarChart3, Cloud, Shield, Briefcase, Heart, Zap, Globe, Coffee, Users, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Static job categories data
const staticJobCategories = [
  { id: "1", title: "Software Engineering", description: "Full-stack, Frontend, Backend, Mobile development roles", icon: "Code", positions_count: 15 },
  { id: "2", title: "Data & Analytics", description: "Data Science, ML Engineering, BI Analyst positions", icon: "BarChart", positions_count: 8 },
  { id: "3", title: "DevOps & Cloud", description: "Cloud Architecture, DevOps, SRE opportunities", icon: "Cloud", positions_count: 6 },
  { id: "4", title: "Cybersecurity", description: "Security Analyst, Penetration Testing, Compliance roles", icon: "Shield", positions_count: 4 },
  { id: "5", title: "Project Management", description: "Technical PM, Scrum Master, Agile Coach positions", icon: "Briefcase", positions_count: 5 },
  { id: "6", title: "QA & Testing", description: "QA Engineer, Automation, Performance Testing roles", icon: "CheckCircle", positions_count: 7 }
];

const staticContactInfo = {
  email: "hello@shareboxtech.com",
  phone: "+44-7438382880",
  address: "3 Thursfield Road",
  city: "Tipton, DY48TJ",
  country: "United Kingdom",
  working_hours: "Monday - Friday: 9:00 AM - 6:00 PM"
};

const iconMap = {
  Code: Code,
  BarChart: BarChart3,
  Cloud: Cloud,
  Shield: Shield,
  Briefcase: Briefcase,
  CheckCircle: CheckCircle,
};

const benefits = [
  { icon: Heart, title: 'Health & Wellness', description: 'Comprehensive health insurance' },
  { icon: Zap, title: 'Growth', description: 'Learning & development paths' },
  { icon: Globe, title: 'Remote', description: 'Flexible work arrangements' },
  { icon: Coffee, title: 'Balance', description: 'Generous PTO policy' },
  { icon: Users, title: 'Team', description: 'Collaborative culture' },
  { icon: Briefcase, title: 'Compensation', description: 'Competitive packages' },
];

export const Careers = () => {
  const [jobCategories, setJobCategories] = useState(staticJobCategories);
  const [contactInfo, setContactInfo] = useState(staticContactInfo);

  useEffect(() => {
    // Try to fetch from API, fallback to static data
    const fetchData = async () => {
      try {
        const [categoriesRes, contactRes] = await Promise.all([
          axios.get(`${API}/job-categories`),
          axios.get(`${API}/contact`)
        ]);
        setJobCategories(categoriesRes.data);
        setContactInfo(contactRes.data);
      } catch (error) {
        // Use static data on error
        console.log('Using static data');
      }
    };
    fetchData();
  }, []);

  const totalPositions = jobCategories.reduce((sum, cat) => sum + cat.positions_count, 0);

  return (
    <div className="min-h-screen bg-[#0a0f1a] pt-20" data-testid="careers-page">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32" data-testid="careers-hero">
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/3184339/pexels-photo-3184339.jpeg"
            alt="Careers"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0f1a] via-[#0a0f1a]/95 to-[#0a0f1a]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 text-center">
          <span className="badge-orange mb-6">{totalPositions}+ Open Positions</span>
          <h1 className="heading-1 mb-6">
            Build Your Career at
            <span className="block text-[#0088CC]">Sharebox</span>
          </h1>
          <p className="body-text max-w-2xl mx-auto mb-8">
            Join our team of talented professionals and help shape the future of technology.
          </p>
          <Link to="/contact">
            <Button 
              className="bg-[#0088CC] hover:bg-[#0099dd] text-white font-medium px-8 py-6 text-base rounded-lg"
              data-testid="careers-hero-cta"
            >
              View Open Positions
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Benefits */}
      <section className="section-padding border-t border-[#1e293b]" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-[#FF6600] text-sm font-medium tracking-wider uppercase mb-4 block">
              Why Sharebox
            </span>
            <h2 className="heading-2">More Than Just a Job</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit) => (
              <div 
                key={benefit.title}
                className="service-card"
                data-testid={`benefit-${benefit.title.toLowerCase()}`}
              >
                <div className="icon-container mb-4">
                  <benefit.icon size={20} />
                </div>
                <h3 className="font-semibold text-white mb-2">{benefit.title}</h3>
                <p className="text-slate-500 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Job Categories */}
      <section className="section-padding border-t border-[#1e293b]" data-testid="job-categories-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-[#FF6600] text-sm font-medium tracking-wider uppercase mb-4 block">
              Opportunities
            </span>
            <h2 className="heading-2">Career Areas</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {jobCategories.map((category) => {
              const IconComponent = iconMap[category.icon] || Briefcase;
              return (
                <div 
                  key={category.id}
                  className="service-card group"
                  data-testid={`job-category-${category.id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="icon-container">
                      <IconComponent size={20} />
                    </div>
                    <span className="badge-orange text-xs">
                      {category.positions_count} roles
                    </span>
                  </div>
                  <h3 className="font-semibold text-white mb-2">{category.title}</h3>
                  <p className="text-slate-500 text-sm">{category.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact for Opportunities */}
      <section className="section-padding border-t border-[#1e293b]" data-testid="careers-contact">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="heading-2 mb-6">Interested in Joining?</h2>
          <p className="body-text mb-8">
            Send us your CV and let's start a conversation.
          </p>
          
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="service-card text-left">
              <h4 className="font-semibold text-white mb-2">Email Us</h4>
              <a 
                href="mailto:careers@shareboxtech.com"
                className="text-[#0088CC] hover:underline"
              >
                careers@shareboxtech.com
              </a>
            </div>
            <div className="service-card text-left">
              <h4 className="font-semibold text-white mb-2">Call Us</h4>
              <a 
                href="tel:+447438382880"
                className="text-[#0088CC] hover:underline"
              >
                +44-7438382880
              </a>
            </div>
          </div>
          
          <Link to="/contact">
            <Button 
              className="bg-[#0088CC] hover:bg-[#0099dd] text-white font-medium px-8 py-6 text-base rounded-lg"
              data-testid="careers-contact-cta"
            >
              Contact Our HR Team
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Careers;
